
import { Heart, MessageCircle, Bookmark, Share2, ThumbsUp, Smile, AlertCircle, Angry } from 'lucide-react';
import { PostActionButton } from './PostActionButton';
import { useState, useEffect } from 'react';
import { 
  handleBookmark, 
  ReactionType, 
  getReactionColor, 
  handleReaction,
  getCurrentReaction,
  getReactionCounts
} from '@/utils/postInteractions';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface PostActionsProps {
  postId: string;
  onComment: () => void;
  initialCommentCount?: number;
  initialBookmarkCount?: number;
}

export const PostActions = ({ 
  postId, 
  onComment,
  initialCommentCount = 0,
  initialBookmarkCount = 0,
}: PostActionsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentReaction, setCurrentReaction] = useState<ReactionType | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(initialBookmarkCount);
  const [showReactions, setShowReactions] = useState(false);
  const [reactionCounts, setReactionCounts] = useState<Array<{type: ReactionType, count: number}>>([]);
  const [reactionUsers, setReactionUsers] = useState<Array<{
    type: ReactionType,
    users: Array<{
      username: string;
      avatar_url: string | null;
    }>
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const reactionIcons = {
    heart: Heart,
    thumbsUp: ThumbsUp,
    smile: Smile,
    surprised: AlertCircle,
    angry: Angry
  } as const;

  useEffect(() => {
    const fetchInitialState = async () => {
      try {
        setIsLoading(true);
        if (user?.id) {
          const reaction = await getCurrentReaction(postId, user.id);
          setCurrentReaction(reaction);
        }
        const counts = await getReactionCounts(postId);
        setReactionCounts(counts || []);

        // Fetch users who reacted
        const { data: reactionsWithUsers } = await supabase
          .from('post_interactions')
          .select(`
            interaction_type,
            profiles (
              username,
              avatar_url
            )
          `)
          .eq('post_id', postId);

        if (reactionsWithUsers) {
          const usersByReaction = reactionsWithUsers.reduce((acc: any, reaction) => {
            const type = reaction.interaction_type as ReactionType;
            if (!acc[type]) {
              acc[type] = [];
            }
            if (reaction.profiles) {
              acc[type].push(reaction.profiles);
            }
            return acc;
          }, {});

          const formattedUsers = Object.entries(usersByReaction).map(([type, users]) => ({
            type: type as ReactionType,
            users: users as Array<{ username: string; avatar_url: string | null }>
          }));

          setReactionUsers(formattedUsers);
        }
      } catch (error) {
        console.error('Error fetching initial state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialState();
  }, [postId, user?.id]);

  const handleReactionClick = async (type: ReactionType) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to interact with posts",
        variant: "destructive"
      });
      return;
    }

    try {
      const newReaction = await handleReaction(postId, user.id, type);
      setCurrentReaction(newReaction);
      setShowReactions(false);

      const counts = await getReactionCounts(postId);
      setReactionCounts(counts || []);
    } catch (error) {
      console.error('Error handling reaction:', error);
      toast({
        title: "Error",
        description: "Failed to update reaction",
        variant: "destructive"
      });
    }
  };

  const handleBookmarkClick = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to bookmark posts",
        variant: "destructive"
      });
      return;
    }

    try {
      const success = await handleBookmark(postId, user.id);
      if (success !== undefined) {
        setIsBookmarked(success);
        setBookmarkCount(prev => success ? prev + 1 : prev - 1);
      }
    } catch (error) {
      console.error('Error handling bookmark:', error);
      toast({
        title: "Error",
        description: "Failed to update bookmark",
        variant: "destructive"
      });
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: 'Check out this post',
        url: `${window.location.origin}/post/${postId}`
      });
    } catch (err) {
      const url = `${window.location.origin}/post/${postId}`;
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied",
        description: "Post link copied to clipboard"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 border-t border-flux-cyan/20">
        <div className="animate-pulse bg-gray-700/20 h-8 w-8 rounded" />
        <div className="animate-pulse bg-gray-700/20 h-8 w-8 rounded" />
        <div className="animate-pulse bg-gray-700/20 h-8 w-8 rounded" />
      </div>
    );
  }

  const totalReactions = reactionCounts.reduce((sum, count) => sum + count.count, 0);

  return (
    <div className="flex items-center gap-4 px-2 py-1.5 border-t border-flux-cyan/20">
      <div className="flex items-center gap-3">
        <Popover open={showReactions} onOpenChange={setShowReactions}>
          <PopoverTrigger asChild>
            <div>
              <PostActionButton
                icon={currentReaction ? reactionIcons[currentReaction] : Heart}
                onClick={() => currentReaction ? handleReactionClick(currentReaction) : setShowReactions(true)}
                className={currentReaction ? getReactionColor(currentReaction) : 'text-gray-400 hover:text-red-500'}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-fit p-2 flex gap-2">
            {Object.entries(reactionIcons).map(([type, Icon]) => (
              <button
                key={type}
                onClick={() => handleReactionClick(type as ReactionType)}
                className={`p-2 rounded-full transition-colors ${
                  currentReaction === type ? getReactionColor(type as ReactionType) : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Icon className="w-5 h-5" />
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {reactionCounts.length > 0 && (
          <HoverCard>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                {reactionCounts.slice(0, 3).map((reaction, index) => {
                  const Icon = reactionIcons[reaction.type];
                  return Icon ? (
                    <div
                      key={reaction.type}
                      className={`${getReactionColor(reaction.type)} bg-black/10 rounded-full p-1`}
                    >
                      <Icon className="w-3 h-3" />
                    </div>
                  ) : null;
                })}
                <span className="text-sm text-gray-400">{totalReactions}</span>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="space-y-4">
                {reactionUsers.map((reactionGroup) => {
                  const Icon = reactionIcons[reactionGroup.type];
                  return (
                    <div key={reactionGroup.type} className="space-y-2">
                      <div className="flex items-center gap-2">
                        {Icon && <Icon className={`w-4 h-4 ${getReactionColor(reactionGroup.type)}`} />}
                        <span className="text-sm font-medium">{reactionGroup.users.length}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {reactionGroup.users.map((user, index) => (
                          <div key={index} className="flex items-center gap-1">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={user.avatar_url || undefined} />
                              <AvatarFallback>{user.username[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{user.username}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
      </div>

      <PostActionButton
        icon={MessageCircle}
        onClick={onComment}
        count={initialCommentCount}
        showCount={true}
        isComment={true}
      />
      <PostActionButton
        icon={Bookmark}
        onClick={handleBookmarkClick}
        count={bookmarkCount}
        className={isBookmarked ? 'text-flux-cyan hover:text-flux-cyan/80' : ''}
        showCount={true}
      />
      <PostActionButton
        icon={Share2}
        onClick={handleShare}
        showCount={false}
      />
    </div>
  );
};
